import React, { useState, useEffect, useContext, useRef } from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { ThreeDots } from "react-loader-spinner";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import AuthContext from "../../../../../connections/auth/AuthProvider";
import { getTopClientsData } from "../../../viewServices/calls/apiCalls";
import {
  grayColor,
  whiteColor,
} from "../../../../../assets/jss/material-dashboard-pro-react";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  tableContainer: {
    flex: 1,
    overflow: "auto",
    border: `1px solid ${grayColor[8]}`,
    borderRadius: "4px",
    backgroundColor: whiteColor,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "11px",
  },
  headerRow: {
    backgroundColor: grayColor[12],
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  headerCell: {
    padding: "8px 12px",
    border: `1px solid ${grayColor[8]}`,
    fontSize: "11px",
    fontWeight: "bold",
    textAlign: "left",
    backgroundColor: grayColor[12],
    "&:first-child": {
      textAlign: "center",
      width: "60px",
    },
    "&:nth-child(3)": {
      textAlign: "right",
    },
    "&:nth-child(4)": {
      textAlign: "right",
    },
    "&:last-child": {
      textAlign: "center",
    },
  },
  dataRow: {
    "&:hover": {
      backgroundColor: grayColor[13],
    },
    "&:nth-child(even)": {
      backgroundColor: grayColor[12],
    },
  },
  dataCell: {
    padding: "8px 12px",
    border: `1px solid ${grayColor[8]}`,
    fontSize: "10px",
    "&:first-child": {
      textAlign: "center",
      fontWeight: "bold",
    },
    "&:nth-child(2)": {
      fontWeight: 500,
    },
    "&:nth-child(3)": {
      textAlign: "right",
      fontWeight: "bold",
    },
    "&:nth-child(4)": {
      textAlign: "right",
      fontWeight: "bold",
    },
    "&:last-child": {
      textAlign: "center",
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    minHeight: "200px",
  },
  noDataMessage: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    padding: theme.spacing(4),
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  errorMessage: {
    textAlign: "center",
    color: theme.palette.error.main,
    padding: theme.spacing(4),
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const TopClients = ({ selectedScenario, appliedFilters, height = "400px" }) => {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;
  const classes = useStyles();
  const T_PREFIX = "rgm.cockpit.topclients";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  const [topClients, setTopClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cache top clients data to avoid refetching on remount
  const cachedTopClientsData = useRef({});

  useEffect(() => {
    const fetchTopClients = async () => {
      if (!selectedScenario) {
        setTopClients([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Create cache key from scenario and filters
        const cacheKey = JSON.stringify({
          selectedScenario,
          appliedFilters,
        });

        // Check cache first
        if (cachedTopClientsData.current[cacheKey]) {
          console.log(
            "✅ Using cached top clients data for scenario:",
            selectedScenario,
          );
          setTopClients(cachedTopClientsData.current[cacheKey]);
          setLoading(false);
          return;
        }

        // Use the new backend API
        const response = await getTopClientsData(
          selectedScenario,
          appliedFilters,
        );

        if (response && response.status === "success" && response.data) {
          // Cache the result
          cachedTopClientsData.current[cacheKey] = response.data;
          setTopClients(response.data);
        } else {
          setTopClients([]);
        }
      } catch (err) {
        console.error("Failed to fetch top clients data:", err);
        setError("Failed to load top clients data");
        setTopClients([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTopClients();
  }, [selectedScenario, appliedFilters]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div style={{ width: "100%", height: height }}>
        <Box className={classes.loadingContainer}>
          <ThreeDots
            height="80"
            width="80"
            radius="9"
            color={clientColor}
            ariaLabel="three-dots-loading"
            wrapperStyle={{}}
            wrapperClassName=""
            visible={true}
          />
        </Box>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ width: "100%", height: height }}>
        <Box className={classes.errorMessage}>
          <Typography variant="body1">{error}</Typography>
        </Box>
      </div>
    );
  }

  if (!selectedScenario) {
    return (
      <div style={{ width: "100%", height: height }}>
        <Box className={classes.noDataMessage}>
          <Typography variant="body1">
            {t("select_scenario", "Select a scenario to view top clients")}
          </Typography>
        </Box>
      </div>
    );
  }

  if (topClients.length === 0) {
    return (
      <div style={{ width: "100%", height: height }}>
        <Box className={classes.noDataMessage}>
          <Typography variant="body1">
            {t("no_data", "No client data available")}
          </Typography>
        </Box>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height: height }}>
      <Box className={classes.container}>
        <Box className={classes.tableContainer}>
          <table className={classes.table}>
            <thead>
              <tr className={classes.headerRow}>
                <th className={classes.headerCell}>Rank</th>
                <th className={classes.headerCell}>Client Name</th>
                <th className={classes.headerCell}>Total Margin</th>
                <th className={classes.headerCell}>Margin %</th>
                <th className={classes.headerCell}>Records</th>
              </tr>
            </thead>
            <tbody>
              {topClients.map((client) => (
                <tr key={client.client_name} className={classes.dataRow}>
                  <td className={classes.dataCell}>#{client.rank}</td>
                  <td className={classes.dataCell}>{client.client_name}</td>
                  <td className={classes.dataCell}>
                    {formatCurrency(client.total_margin_value)}
                  </td>
                  <td className={classes.dataCell}>
                    {formatPercentage(client.margin_percentage)}
                  </td>
                  <td className={classes.dataCell}>{client.record_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Box>
    </div>
  );
};

TopClients.propTypes = {
  selectedScenario: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  appliedFilters: PropTypes.shape({
    product: PropTypes.string,
    brand: PropTypes.string,
    channel: PropTypes.string,
    region: PropTypes.string,
    client: PropTypes.string,
  }),
  height: PropTypes.string,
};

export default TopClients;
